import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  FormControlLabel,
  Checkbox,
  Box,
  Divider
} from '@mui/material';
import { useCookieConsent } from '../hooks/useCookieConsent';

/**
 * Cookie settings dialog component
 * Allows users to customize their cookie preferences
 */
const CookieSettings = ({ open, onClose }) => {
  const { consent, saveConsent, acceptAll } = useCookieConsent();
  const [localConsent, setLocalConsent] = useState({
    necessary: true,
    analytics: false,
    marketing: false
  });

  // Update local state when consent changes
  useEffect(() => {
    if (open) {
      setLocalConsent({
        necessary: consent.necessary,
        analytics: consent.analytics,
        marketing: consent.marketing
      });
    }
  }, [open, consent]);

  const handleChange = (category) => (event) => {
    if (category === 'necessary') {
      // Necessary cookies cannot be disabled
      return;
    }
    setLocalConsent({
      ...localConsent,
      [category]: event.target.checked
    });
  };

  const handleSave = () => {
    saveConsent(localConsent);
    onClose();
  };

  const handleAcceptAll = () => {
    acceptAll();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2
        }
      }}
    >
      <DialogTitle>
        <Typography variant="h6" component="div">
          Ustawienia plików cookie
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Możesz zarządzać preferencjami dotyczącymi plików cookie. Niezbędne pliki cookie są zawsze włączone,
          ponieważ są wymagane do prawidłowego działania strony.
        </Typography>

        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={localConsent.necessary}
                disabled
                sx={{ '&.Mui-disabled': { color: 'primary.main' } }}
              />
            }
            label={
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  Niezbędne pliki cookie
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Te pliki cookie są niezbędne do prawidłowego działania strony. Nie można ich wyłączyć.
                  Zazwyczaj są ustawiane tylko w odpowiedzi na Twoje działania, takie jak logowanie lub
                  wypełnianie formularzy.
                </Typography>
              </Box>
            }
            sx={{ alignItems: 'flex-start', mb: 2 }}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={localConsent.analytics}
                onChange={handleChange('analytics')}
              />
            }
            label={
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  Pliki cookie analityczne
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Te pliki cookie pozwalają nam analizować sposób korzystania ze strony, abyśmy mogli
                  poprawić jej funkcjonalność i doświadczenie użytkownika.
                </Typography>
              </Box>
            }
            sx={{ alignItems: 'flex-start', mb: 2 }}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={localConsent.marketing}
                onChange={handleChange('marketing')}
              />
            }
            label={
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  Pliki cookie marketingowe
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Te pliki cookie są używane do wyświetlania spersonalizowanych reklam i śledzenia
                  skuteczności kampanii marketingowych.
                </Typography>
              </Box>
            }
            sx={{ alignItems: 'flex-start' }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose} variant="outlined">
          Anuluj
        </Button>
        <Button onClick={handleAcceptAll} variant="outlined" color="primary">
          Akceptuj wszystkie
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Zapisz preferencje
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CookieSettings;

